import React, { useState, useEffect } from 'react';
import {
    MdFileDownload,
    MdTrendingUp,
    MdPerson,
    MdPeople,
    MdDescription,
    MdCurrencyRupee,
    MdSearch,
    MdFilterList,
    MdStore,
    MdExpandMore,
    MdExpandLess
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './VendorSummaryReport.module.css';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import TableLimitSelector from '../components/common/TableLimitSelector';
import Drawer from '../components/common/Drawer';
import PaginatedSelect from '../components/common/PaginatedSelect';
import { authFetch } from '../components/utils/authFetch';
import { GET_VENDOR_PAYMENT_REPORT_API, GET_ALL_VENDORS_API } from '../config/API';

const VendorSummaryReport = () => {
    const navigate = useNavigate();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(null);

    // Stats state
    const [stats, setStats] = useState({
        totalVendors: 0,
        totalPOs: 0,
        totalAmount: 0,
        totalPaidAmount: 0,
        totalRemainingAmount: 0
    });

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showStats, setShowStats] = useState(window.innerWidth > 768);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (!mobile) setShowStats(true);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchReports = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?page=${currentPage}&limit=${limit}&search=${debouncedSearch}`;

            if (selectedVendor?.value) {
                queryParams += `&vendorId=${selectedVendor.value}`;
            }

            const response = await authFetch(
                `${GET_VENDOR_PAYMENT_REPORT_API}${queryParams}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const result = await response.json();

            if (result.status) {
                setReports(result.data || []);
                setStats(result.summary || {
                    totalVendors: 0,
                    totalPOs: 0,
                    totalAmount: 0,
                    totalPaidAmount: 0,
                    totalRemainingAmount: 0
                });

                const pagination = result.pagination;
                setTotalPages(pagination?.totalPages || 1);
                setTotalRecords(pagination?.totalCount || result.data?.length || 0);
            } else {
                setReports([]);
                setStats({
                    totalVendors: 0,
                    totalPOs: 0,
                    totalAmount: 0,
                    totalPaidAmount: 0,
                    totalRemainingAmount: 0
                });
                setTotalPages(1);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error('Error fetching vendor payment report:', error);
            setReports([]);
        } finally {
            setLoading(false);
        }
    };

    const handleExportCSV = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?exportCSV=true&search=${debouncedSearch}`;

            if (selectedVendor?.value) {
                queryParams += `&vendorId=${selectedVendor.value}`;
            }

            const response = await authFetch(`${GET_VENDOR_PAYMENT_REPORT_API}${queryParams}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const result = await response.json();

            if (result.status) {
                toast.success(result.message || 'Export started! Check Report Log for the file.');
            } else {
                toast.error(result.message || 'Failed to start export');
            }
        } catch (error) {
            console.error('Error exporting CSV:', error);
            toast.error('An error occurred during export');
        }
    };

    useEffect(() => {
        fetchReports();
    }, [currentPage, debouncedSearch, selectedVendor, limit]);

    const formatCurrency = (amount) => {
        const val = parseFloat(amount || 0);
        return `₹${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    return (
        <div className={styles['purchase-orders-container']}>

            {/* Header Section */}
            <div className={styles['purchase-orders-header']}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div>
                        <h2>Vendor Summary Report</h2>
                        <p className={styles['page-subtitle']}>Comprehensive overview of vendor engagement, commitments, and payment status.</p>
                    </div>
                </div>
                <div className={styles['header-actions']}>
                    <button
                        className={`${styles['filter-btn']} ${showFilters ? styles.active : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <MdFilterList /> Filter
                    </button>
                    <button className={styles['export-btn']} onClick={handleExportCSV}>
                        <MdFileDownload size={18} /> Export CSV
                    </button>
                </div>
            </div>

            <Drawer
                isOpen={showFilters}
                onClose={() => setShowFilters(false)}
                title="Filter Reports"
            >
                <div className={styles['drawer-filter-content']}>
                    <div className={styles['filter-item']}>
                        <label>Vendor Name</label>
                        <PaginatedSelect
                            apiUrl={GET_ALL_VENDORS_API}
                            mapResponse={(v) => ({
                                value: v.id,
                                label: v.vendorName
                            })}
                            value={selectedVendor}
                            onChange={(option) => setSelectedVendor(option)}
                            placeholder="Select Vendor"
                            isClearable
                        />
                    </div>
                    <div className={styles['drawer-actions']}>
                        <button
                            className={styles['clear-filters-btn']}
                            onClick={() => {
                                setSelectedVendor(null);
                            }}
                            style={{ width: '100%' }}
                        >
                            Reset Filter
                        </button>
                    </div>
                </div>
            </Drawer>

            {/* Mobile Stats Toggle */}
            {isMobile && (
                <button
                    className={styles['stats-toggle-btn']}
                    onClick={() => setShowStats(!showStats)}
                >
                    {showStats ? (
                        <>
                            <MdExpandLess /> Hide Summary
                        </>
                    ) : (
                        <>
                            <MdExpandMore /> Show Summary
                        </>
                    )}
                </button>
            )}

            {/* Stats Grid */}
            <div className={`${styles['stats-grid']} ${showStats ? styles['show'] : styles['hide']}`}>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['blue']}`}>
                            <MdStore />
                        </div>
                    </div>
                    <div className={styles['stat-title']}>Total Vendors</div>
                    <div className={styles['stat-value']}>{stats.totalVendors}</div>
                </div>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['purple']}`}>
                            <MdDescription />
                        </div>
                    </div>
                    <div className={styles['stat-title']}>Total POs</div>
                    <div className={styles['stat-value']}>{stats.totalPOs}</div>
                </div>

                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['blue']}`}>
                            <MdCurrencyRupee />
                        </div>
                    </div>
                    <div className={styles['stat-title']}>Total Committed</div>
                    <div className={styles['stat-value']}>{formatCurrency(stats.totalAmount)}</div>
                </div>

                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['green']}`}>
                            <MdCurrencyRupee />
                        </div>
                    </div>
                    <div className={styles['stat-title']}>Total Paid</div>
                    <div className={styles['stat-value']}>{formatCurrency(stats.totalPaidAmount)}</div>
                </div>

                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['red']}`}>
                            <MdCurrencyRupee />
                        </div>
                    </div>
                    <div className={styles['stat-title']}>Remaining Amount</div>
                    <div className={`${styles['stat-value']} ${styles['remaining-value']}`}>{formatCurrency(stats.totalRemainingAmount)}</div>
                </div>
            </div>

            {/* Main Content Table Card */}
            <div className={!isMobile ? styles['card-container-po'] : ''}>
                <div className={styles['search-container']}>
                    <TableLimitSelector limit={limit} onLimitChange={handleLimitChange} />
                    <div className={styles['search-wrapper']}>
                        <MdSearch className={styles['search-icon']} />
                        <input
                            type="text"
                            placeholder="Search vendors..."
                            className={styles['search-input']}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {!isMobile ? (
                    <div className={styles['table-container']}>
                        <table>
                            <thead>
                                <tr>
                                    <th>Vendor Name</th>
                                    <th className={styles['text-center']}>Total Project</th>
                                    <th className={styles['text-center']}>Total POs</th>
                                    <th className={styles['text-right']}>Total Amount</th>
                                    <th className={styles['text-right']}>Total Paid Amount</th>
                                    <th className={styles['text-right']}>Total Remaining Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '3rem' }}>
                                            <Loader text="Fetching vendor summary..." />
                                        </td>
                                    </tr>
                                ) : reports.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '3rem' }}>
                                            <NoData title="No Reports Found" message="Try adjusting your search filters." />
                                        </td>
                                    </tr>
                                ) : (
                                    reports.map((vendor) => (
                                        <tr key={vendor.vendorId}>
                                            <td>
                                                <div className={styles['vendor-info']}>
                                                    <div className={styles['vendor-name']}>{vendor.vendorName}</div>
                                                    <div className={styles['vendor-company']}>{vendor.companyName}</div>
                                                </div>
                                            </td>
                                            <td className={styles['text-center']}>
                                                <span 
                                                    className={styles['link-text']}
                                                    onClick={() => window.open(`/reports/vendor/${vendor.vendorId}/project-summary`, '_blank')}
                                                >
                                                    {vendor.totalProjects}
                                                </span>
                                            </td>
                                            <td className={styles['text-center']}>
                                                <span 
                                                    className={styles['link-text']}
                                                    onClick={() => window.open(`/reports/vendor/${vendor.vendorId}/po-summary`, '_blank')}
                                                >
                                                    {vendor.totalPOs}
                                                </span>
                                            </td>
                                            <td className={`${styles['text-right']} ${styles['amount-cell-grandTotal']}`}>{formatCurrency(vendor.totalAmount)}</td>
                                            <td className={`${styles['text-right']} ${styles['amount-cell-paid']}`}>{formatCurrency(vendor.totalPaidAmount)}</td>
                                            <td className={`${styles['text-right']} ${styles['amount-cell-remaining']}`}>{formatCurrency(vendor.totalRemainingAmount)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={styles['mobile-card-view']}>
                        {loading ? (
                            <div style={{ padding: '2rem' }}>
                                <Loader text="Fetching reports..." />
                            </div>
                        ) : reports.length === 0 ? (
                            <NoData title="No Reports Found" message="Try adjusting your filters." />
                        ) : (
                            reports.map((vendor) => (
                                <div key={vendor.vendorId} className={styles['project-card']}>
                                    <div className={styles['card-header']}>
                                        <div className={styles['card-title']}>
                                            <strong>{vendor.vendorName}</strong>
                                            <span className={styles['vendor-company']}>{vendor.companyName}</span>
                                        </div>
                                    </div>
                                    <div className={styles['card-body']}>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Total Projects:</span>
                                            <span
                                                className={`${styles['card-value']} ${styles['link-text']}`}
                                                onClick={() => window.open(`/reports/vendor/${vendor.vendorId}/project-summary`, '_blank')}
                                            >
                                                {vendor.totalProjects}
                                            </span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Total POs:</span>
                                            <span 
                                                className={`${styles['card-value']} ${styles['link-text']}`}
                                                onClick={() => window.open(`/reports/vendor/${vendor.vendorId}/po-summary`, '_blank')}
                                            >
                                                {vendor.totalPOs}
                                            </span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Total Amount:</span>
                                            <span className={`${styles['card-value']} ${styles['amount-cell-grandTotal']}`}>
                                                {formatCurrency(vendor.totalAmount)}
                                            </span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Paid Amount:</span>
                                            <span className={`${styles['card-value']} ${styles['amount-cell-paid']}`}>
                                                {formatCurrency(vendor.totalPaidAmount)}
                                            </span>
                                        </div>
                                        <div className={styles['card-row']}>
                                            <span className={styles['card-label']}>Remaining:</span>
                                            <span className={`${styles['card-value']} ${styles['amount-cell-remaining']}`}>
                                                {formatCurrency(vendor.totalRemainingAmount)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                <Pagination limit={limit} onLimitChange={handleLimitChange}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    onPageChange={(page) => setCurrentPage(page)}
                    currentCount={reports.length}
                    entityName="Vendor Summary"
                />
            </div>
        </div>
    );
};

export default VendorSummaryReport;
